import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import IframeStyledHOC from './IframeStyledHOC';
import { IframeStyled, Loader, Logo, Root, Spinner } from './style';
import { Props } from './types';

const DURATION = 6000;
const MAX_ATTEMPTS = 10;

function isPdfUrl(url: string) {
  return /\.pdf($|&embedded=true$)/i.test(url);
}

function isGoogleDocsUrl(url: string) {
  return /docs\.google\.com/i.test(url);
}

const Iframe: FC<Props> = ({ src, logoSrc, useModernPlayer, ...rest }) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [iframeSrc, setIframeSrc] = useState(src);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const attempts = useRef(0);
  const intervalRef = useRef(null);

  const runPdfLogic = useMemo(() => isPdfUrl(src) && isGoogleDocsUrl(src), [src]);

  const removeInterval = useCallback(() => {
    if (intervalRef.current) clearInterval(intervalRef.current);
  }, []);

  const resetIframe = useCallback(() => {
    if (attempts.current > MAX_ATTEMPTS) {
      removeInterval();
      toast.info('PDF file could not be loaded. Try reopening the tag!');
      setLoading(false);
      return;
    }

    // To force iframe pdf file to reload
    setIframeSrc('');
    setTimeout(() => {
      setIframeSrc(src);
    }, 500);

    attempts.current += 1;
  }, [src, removeInterval]);

  const createInterval = useCallback(() => {
    if (!runPdfLogic) return;

    intervalRef.current = setInterval(() => {
      resetIframe();
    }, DURATION);
  }, [resetIframe, runPdfLogic]);

  const onLoad = useCallback(() => {
    if (!runPdfLogic) return;

    removeInterval();
    // this is more of a hack than a proper solution to check if the iframe has loaded
    // contentWindow.lenght will be 0 if the iframe has not loaded and 2 if it has
    const contentLength = iframeRef.current?.contentWindow.length;
    if (!contentLength) {
      resetIframe();
      createInterval();
      return;
    }

    setLoading(false);
  }, [createInterval, resetIframe, removeInterval, runPdfLogic]);

  useEffect(() => {
    if (src && isPdfUrl(src)) {
      createInterval();
      setLoading(true);
    } else if (src) {
      setIframeSrc(src);
    }

    return () => {
      removeInterval();
    };
  }, [createInterval, src, removeInterval]);

  return (
    <Root>
      {loading && (
        <Loader>
          <Spinner />
          <Logo>
            <img src={logoSrc} alt="" />
          </Logo>
        </Loader>
      )}
      {useModernPlayer ? (
        <IframeStyledHOC width="100%" height="100%" src={src} onLoad={onLoad} {...rest} />
      ) : (
        <IframeStyled
          ref={iframeRef}
          width="100%"
          height="100%"
          src={iframeSrc}
          allow="geolocation;camera;microphone"
          onLoad={onLoad}
          {...rest}
        />
      )}
    </Root>
  );
};

export default Iframe;
